import { axiosInstance } from './api';

/**
 * Vendor-API parameter tweaks for cameras (issue #34 part 3).
 *
 * Mirrors the BE endpoint at
 * ``services/plugins/equipment_discovery/api/vendor_params.py``. The
 * schema is **fully BE-driven** — we probe the device's own ISAPI
 * capabilities and surface only knobs it declares supporting, with
 * the constraints (enum opts / int min-max / bool) it ships. Adding
 * support for a new Hik model is zero code change on our side as
 * long as the device follows the standard ISAPI capabilities schema.
 *
 * The response can carry multiple ``targets`` — currently always
 * one entry (``nvr`` — NVR's cached config); Phase 2 will add a
 * ``camera`` target when the IPC's direct ISAPI is reachable.
 *
 * No DB persistence: GET fetches live state from the device via the
 * jetson; PATCH writes back through the same path. Both run through
 * a single read-modify-write because Hik resets unspecified tags on
 * sparse PUTs.
 */

export type VendorParamKind = 'int' | 'bool' | 'enum';

export interface VendorParamSpec {
  /** Dot-separated XML path, e.g. "WDR.WDRLevel". */
  key: string;
  /** XML path as an array — same info as `key` but pre-split. */
  path: string[];
  /** Top-level Hik ImageChannel group, e.g. "WDR", "BLC". */
  group: string;
  kind: VendorParamKind;
  /** For int: device-declared range. */
  min?: number;
  max?: number;
  /** For enum: the exact option list the device shipped, in its own order. */
  options?: string[];
}

export type VendorParamValue = number | boolean | string;

export interface VendorParamsTarget {
  /** Stable id for FE switching. */
  id: 'nvr' | 'camera';
  /** i18n key for the target name. */
  label_key: string;
  schema: VendorParamSpec[];
  values: Record<string, VendorParamValue | null>;
  errors: string[];
}

export interface VendorParamsResponse {
  vendor: string;
  targets: VendorParamsTarget[];
  /** When present, surfaces a global error e.g. "both targets unreachable". */
  errors?: string[];
}

export interface VendorParamsPatchResponse {
  written: Record<string, VendorParamValue>;
  errors: string[];
}

const url = (id: number | string) =>
  `/equipment_discovery/cameras/${id}/vendor-params/`;

export const getCameraVendorParams = (id: number | string) =>
  axiosInstance.get<VendorParamsResponse>(url(id));

export const patchCameraVendorParams = (
  id: number | string,
  payload: Record<string, VendorParamValue>,
  target: 'nvr' | 'camera' = 'nvr',
) => axiosInstance.patch<VendorParamsPatchResponse>(
  url(id), payload, { params: { target } },
);
